import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Lock, Download, RefreshCw, Users, ShieldCheck, Mail, ArrowLeft, LogOut } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rows, setRows] = useState(null);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');

  // Prevent this page from being indexed/archived by any crawler.
  React.useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow, noarchive, nosnippet, noimageindex';
    document.head.appendChild(meta);
    return () => { document.head.removeChild(meta); };
  }, []);

  const authHeaders = () => ({ 'X-Admin-Email': email.trim(), 'X-Admin-Password': password });

  const signOut = React.useCallback((msg) => {
    setRows(null); setCount(0); setPassword('');
    if (msg) setError(msg);
  }, []);

  // Auto sign-out after 5 minutes of inactivity (only while signed in)
  const authed = rows !== null;
  React.useEffect(() => {
    if (!authed) return;
    let timer;
    const reset = () => {
      clearTimeout(timer);
      timer = setTimeout(() => signOut('Signed out due to inactivity.'), 5 * 60 * 1000);
    };
    const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
    events.forEach((ev) => window.addEventListener(ev, reset, { passive: true }));
    reset();
    return () => { clearTimeout(timer); events.forEach((ev) => window.removeEventListener(ev, reset)); };
  }, [authed, signOut]);

  const load = async (e) => {
    e?.preventDefault();
    if (!email.trim() || !password || loading) return;
    setLoading(true); setError('');
    try {
      const res = await axios.get(`${API}/early-access/list`, { headers: authHeaders() });
      setRows(res.data.signups || []);
      setCount(res.data.count || 0);
    } catch (err) {
      const msg = err?.response?.status === 401 ? 'Invalid email or password.' : 'Could not load. Try again.';
      setError(msg); setRows(null);
    } finally {
      setLoading(false);
    }
  };

  const downloadCsv = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      const res = await axios.get(`${API}/early-access/export`, { headers: authHeaders(), responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url; a.download = 'xincity-early-access.csv';
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Export failed. Please sign in again.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#070609] text-white">
      <div className="max-w-3xl mx-auto px-4 py-14">
        <button onClick={() => navigate('/city')} className="flex items-center gap-2 text-sm text-white/60 hover:text-white transition-colors mb-6">
          <ArrowLeft className="h-4 w-4" /> Back to city
        </button>
        <div className="flex items-center gap-2 mb-1">
          <span className="font-display font-700 tracking-[0.2em]">XINCITY<sup className="text-[0.5em] align-super text-white/60">™</sup></span>
          <span className="text-[10px] px-2 py-0.5 rounded-full border border-[#B026FF]/30 text-[#B026FF]">ADMIN</span>
        </div>
        <h1 className="font-display font-700 text-2xl">Early Access Signups</h1>
        <p className="text-sm text-white/50 mt-1">Sign in to view and export captured emails.</p>

        <form onSubmit={load} className="mt-6 space-y-3 max-w-md">
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
            <input type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email"
              className="w-full bg-white/[0.04] border border-white/10 rounded-lg pl-10 pr-4 py-3 text-sm text-white placeholder:text-white/35 focus:border-[#B026FF]/50 outline-none" />
          </div>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
            <input type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password"
              className="w-full bg-white/[0.04] border border-white/10 rounded-lg pl-10 pr-4 py-3 text-sm text-white placeholder:text-white/35 focus:border-[#B026FF]/50 outline-none" />
          </div>
          <button type="submit" disabled={loading} className="btn-neon rounded-lg px-6 py-3 font-600 flex items-center justify-center gap-2 disabled:opacity-60 w-full">
            {loading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />} Sign in
          </button>
        </form>
        {error && <p className="text-sm mt-3" style={{ color: '#FF5470' }}>{error}</p>}

        {rows && (
          <div className="mt-8">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2 text-white/80"><Users className="h-4 w-4 text-[#B026FF]" /> {count} signup{count !== 1 ? 's' : ''}</div>
              <div className="flex items-center gap-2">
                <button onClick={downloadCsv} disabled={downloading} className="btn-gold rounded-lg px-4 py-2 text-sm font-600 flex items-center gap-2 disabled:opacity-60"><Download className="h-4 w-4" /> {downloading ? 'Preparing...' : 'Download CSV'}</button>
                <button onClick={() => signOut('You have been signed out.')} className="btn-ghost rounded-lg px-4 py-2 text-sm font-500 flex items-center gap-2"><LogOut className="h-4 w-4" /> Log out</button>
              </div>
            </div>
            <div className="rounded-xl border border-white/10 overflow-hidden">
              <div className="grid grid-cols-[1fr_auto_auto] gap-3 px-4 py-2.5 text-[11px] uppercase tracking-wide text-white/40 bg-white/[0.03]">
                <span>Email</span><span>18+</span><span>When</span>
              </div>
              {rows.length === 0 ? (
                <div className="px-4 py-10 text-center text-white/40 text-sm">No signups yet.</div>
              ) : rows.map((r, i) => (
                <div key={r.id || i} className="grid grid-cols-[1fr_auto_auto] gap-3 px-4 py-3 text-sm border-t border-white/5 items-center">
                  <span className="text-white/90 truncate">{r.email}</span>
                  <span className="text-[#2FBF71] flex items-center"><ShieldCheck className="h-4 w-4" /></span>
                  <span className="text-white/45 text-xs whitespace-nowrap">{String(r.created_at).slice(0, 16).replace('T', ' ')}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
